import {
  MouseEventHandler,
  RefObject,
  forwardRef,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  BoxProps,
  ButtonIcon,
  ContainerCarouselViews,
  IFrameYoutube,
  Image,
  SlickSlider,
} from '@core/component';
import { useContextMediaQuery, useContextRouting } from '@core/context';
import { useLanguage } from '@core/hook';
import { EFilenameBlur, getStaticPathImage, getStaticPathImageBlurData } from '@core/media';
import { urlFor } from '@core/sanity';
import { EIcon, EPosition, ESize } from '@core/type';
import { EMedia, MediaEntity, ProductBlockEntity } from '@core/type/api';
import { SanityMedia } from '@core/type/sanity';

import { ProductCarouselImageContainer } from './ProductCarouselImageContainer';
import { ProductCarouselLabels } from './ProductCarouselLabels';
import { DESKTOP_IMAGE_SIZE, MOBILE_IMAGE_SIZE } from './constants';
import { COUNT_OF_MEDIA_PER_SLIDE, useCarouselProducts } from './use-carousel-products';

const PERCENTAGE = 100;
const SLIDES_MOBILE_LIMIT = 9;

const ImageContainer = forwardRef<
  HTMLDivElement,
  BoxProps & { size: string; product: Omit<ProductBlockEntity, 'medias'> }
>(({ children, size, product, ...restProps }, ref) => {
  return (
    <Box
      overflow="hidden"
      width={'100%'}
      height={size}
      maxWidth={size}
      ref={ref}
      position="relative"
      {...restProps}
    >
      {children}
    </Box>
  );
});

ImageContainer.displayName = 'ImageContainer';

export const ProductCarousel = memo(
  ({
    ...product
  }: Omit<ProductBlockEntity, 'medias'> & { medias: SanityMedia[] | MediaEntity[] }) => {
    const { medias, isNew, isTopSales, name, deal, isOutOfStock, isEnabled } = product;

    const containerRef = useRef(null) as RefObject<HTMLDivElement>;
    const [mediaIndex, setMediaIndex] = useState<number>(0);
    const [imagePosition, setImagePosition] = useState<string>('50%');
    const { keyRoute } = useContextRouting();
    const { language } = useLanguage();
    const { isMobile } = useContextMediaQuery();
    const {
      totalSteps,
      slides,
      handleNext,
      handlePrevious,
      setSlide,
      currentStep,
      containerRef: carouselContainerRef,
      ...restProps
    } = useCarouselProducts(isMobile ? medias.slice(0, SLIDES_MOBILE_LIMIT) : medias);

    const isMediaPresent = totalSteps > 0;
    const imageSize = isMobile ? MOBILE_IMAGE_SIZE : DESKTOP_IMAGE_SIZE;

    const handleImagePosition: MouseEventHandler = (e) => {
      if (containerRef?.current) {
        const { height, width, top, left } = containerRef.current.getBoundingClientRect();
        setImagePosition(
          `${((e.clientX - left) / width) * PERCENTAGE}% ${
            ((e.clientY - top) / height) * PERCENTAGE
          }%`,
        );
      }
    };

    useEffect(() => {
      setSlide(0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyRoute]);

    const mediaProps = useMemo(() => {
      return medias[mediaIndex];
    }, [medias, mediaIndex]);

    const containerWidth = carouselContainerRef?.current?.offsetWidth || '100%';

    const getDefaultImage = (name: string) => (
      <ImageContainer product={product} size={imageSize}>
        <Image
          src={getStaticPathImage('card/product-default.png')}
          blurDataURL={getStaticPathImageBlurData(EFilenameBlur['card/product-default.png'])}
          title={name}
          alt={name}
          priority
        />
      </ImageContainer>
    );

    const getImageComponent = (mediaProps: SanityMedia | MediaEntity, priority: boolean) => {
      if (!mediaProps || !isMediaPresent) {
        return getDefaultImage(name);
      }

      const isSanityMedia = '_type' in mediaProps;
      const isImage =
        (isSanityMedia && mediaProps._type === 'image') ||
        (!isSanityMedia && mediaProps.mediaType === EMedia.IMAGE);

      const url = isSanityMedia
        ? isImage
          ? urlFor(mediaProps)
          : mediaProps.url
        : mediaProps.mediaUrl;
      const alt = 'alt' in mediaProps ? mediaProps.alt : '';
      const title = 'title' in mediaProps ? mediaProps.title : '';

      return isImage ? (
        <ImageContainer product={product} size={imageSize} ref={containerRef}>
          <ProductCarouselImageContainer transformProps={imagePosition}>
            <Image
              src={url}
              alt={alt}
              title={title}
              priority={priority}
              sizes={`(max-width: 768px) ${MOBILE_IMAGE_SIZE}, ${DESKTOP_IMAGE_SIZE}`}
            />
          </ProductCarouselImageContainer>
        </ImageContainer>
      ) : (
        <IFrameYoutube
          title={title}
          src={url}
          language={language}
          boxProps={{
            width: imageSize,
            height: imageSize,
            maxWidth: '100%',
          }}
        />
      );
    };

    return (
      <Box position={'relative'}>
        <ProductCarouselLabels
          isNew={isNew}
          isTopSales={isTopSales}
          deal={deal}
          isOutOfStock={isOutOfStock}
          isEnabled={isEnabled}
        />

        {!isMobile && (
          <div onMouseMove={handleImagePosition}>
            <Box margin={'0 auto 48px'} maxWidth={'100%'} display="flex" justifyContent="center">
              {getImageComponent(mediaProps, true)}
            </Box>
          </div>
        )}

        <Box display={'flex'} alignItems={'center'}>
          {!isMobile && totalSteps > 1 && (
            <Box width={'32px'} flexShrink={0}>
              <ButtonIcon
                iconProps={{ type: EIcon.ANGLE, orientation: EPosition.LEFT, size: ESize.MD }}
                onClick={handlePrevious}
              />
            </Box>
          )}
          <Box
            ref={carouselContainerRef}
            height={isMobile ? '357px' : '64px'}
            flexGrow={1}
            overflow={'hidden'}
            padding={isMobile ? '0' : '0 16px'}
            position={'relative'}
          >
            <Box position={'absolute'} display={'flex'} left={'0'} right={'0'}>
              {slides.length > 0 ? (
                <ContainerCarouselViews {...restProps}>
                  {slides.map((slideMediaProps, index) => (
                    <Box
                      key={index}
                      display={'flex'}
                      justifyContent={'center'}
                      gap={16}
                      width={containerWidth ? containerWidth : 'auto'}
                      margin={'auto'}
                      flexShrink={0}
                    >
                      {isMobile ? (
                        getImageComponent(slideMediaProps as SanityMedia, index === 0)
                      ) : isMediaPresent ? (
                        (slideMediaProps as SanityMedia[]).map((media, index) => (
                          <Box
                            key={index}
                            width="64px"
                            height="64px"
                            cursor="pointer"
                            position="relative"
                            flexShrink={0}
                            onClick={() =>
                              setMediaIndex(currentStep * COUNT_OF_MEDIA_PER_SLIDE + index)
                            }
                          >
                            {media._type === 'image' ? (
                              <Image
                                src={urlFor(media)}
                                alt={'alt' in media && (media.alt as string)}
                                title={'title' in media && (media.title as string)}
                                width={64}
                                height={64}
                              />
                            ) : (
                              <IFrameYoutube
                                title={media.title}
                                src={media.url}
                                boxProps={{
                                  width: '64px',
                                  height: '64px',
                                  pointerEvents: 'none',
                                }}
                              />
                            )}
                          </Box>
                        ))
                      ) : (
                        <Box width="64px" height="64px" position="relative">
                          {getDefaultImage(name)}
                        </Box>
                      )}
                    </Box>
                  ))}
                </ContainerCarouselViews>
              ) : (
                isMobile && (
                  <Box
                    display={'flex'}
                    justifyContent={'center'}
                    width={containerWidth ? containerWidth : 'auto'}
                    margin={'auto'}
                    flexShrink={0}
                  >
                    {getDefaultImage(name)}
                  </Box>
                )
              )}
            </Box>
          </Box>
          {!isMobile && totalSteps > 1 && (
            <Box width={'32px'} flexShrink={0}>
              <ButtonIcon
                iconProps={{ type: EIcon.ANGLE, orientation: EPosition.RIGHT, size: ESize.MD }}
                onClick={handleNext}
              />
            </Box>
          )}
        </Box>

        {isMobile && totalSteps > 1 && (
          <SlickSlider
            totalSteps={totalSteps}
            currentStep={currentStep}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onClick={setSlide}
            withCarousel
            marginTop={'24px'}
          />
        )}
      </Box>
    );
  },
);

ProductCarousel.displayName = 'ProductCarousel';
